import { Alert, Stack } from '@mui/material';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { MemoGeneratingCallout } from '@/modules/memos/components/MemoGeneratingCallout';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { ArtifactComposer } from './components/ArtifactComposer/ArtifactComposer';
import { ArtifactsTable } from './components/ArtifactsTable/ArtifactsTable';
import { DiagramEditorTrowserMount } from './components/DiagramEditor/DiagramEditorTrowserMount';
import { useArtifactStudioContext } from './contexts/artifactStudio.context';
import { ArtifactStudioProvider } from './contexts/ArtifactStudio.provider';
import { useIsArtifactStudioAvailable } from './hooks/useIsArtifactStudioAvailable';

export function ArtifactStudio({ householdId }: { householdId: string }) {
  const isAvailable = useIsArtifactStudioAvailable();
  if (!isAvailable)
    return (
      <Alert severity="info">
        Artifact builder is not available for this household.
      </Alert>
    );
  return (
    <ArtifactStudioProvider householdId={householdId}>
      <ArtifactStudioContent />
    </ArtifactStudioProvider>
  );
}

/** Lays out the composer, the saved artifacts, and the editor trowser. */
export function ArtifactStudioContent() {
  const { householdId, isEditorOpen, openArtifact } =
    useArtifactStudioContext();
  const isMemosInStudioEnabled = useFeatureFlag('memos_in_studio');
  const navigate = useNavigate();
  // Diagrams open in the editor trowser. Memos open on their details page.
  const openMemo = useCallback(
    (memoId: string) =>
      navigate(
        getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER_MEMO_DETAILS,
          {
            householdId,
            memoId,
          }
        )
      ),
    [householdId, navigate]
  );

  return (
    <Stack height="100%" minHeight={0}>
      <Stack spacing={3}>
        {isMemosInStudioEnabled && <MemoGeneratingCallout />}
        <ArtifactComposer />
        {!isEditorOpen && (
          <ArtifactsTable
            householdId={householdId}
            onOpenDiagram={openArtifact}
            onOpenMemo={openMemo}
          />
        )}
      </Stack>
      <DiagramEditorTrowserMount />
    </Stack>
  );
}
